// packages/ui/src/forms/fields/PhoneInput.tsx
'use client';

import { useRef, type ReactElement, type ReactNode } from 'react';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { Box, Typography } from '@mui/material';
import { useFormContext, useWatch, type FieldValues, type Path, type PathValue } from 'react-hook-form';

import { PHONE_NUMBER_MAX_LENGTH } from '@tourtask/constants/inputs/phone';
import { sanitizePhoneNumberInput } from '@tourtask/utils/shared/phones/sanitizePhoneNumberInput';
import { SmoothCollapse } from '@tourtask/ui/components/SmoothCollapse';
import { FormTextField, type FormTextFieldProps } from './FormTextField';
import { CountrySelectorInput, type CountrySelectorInputComponent } from './CountrySelectorInput';
import { sharedFieldRowSx, sharedLeftIconBoxSx } from './_styles';

import type { CountryWithName } from '@tourtask/types/configs/Country';

type Props<TFieldValues extends FieldValues> = {
  countryCodeName: Path<TFieldValues>;
  numberName: Path<TFieldValues>;
  countriesWithNames: CountryWithName[];
  countryLabel: string;
  numberLabel: string;
  disabled?: boolean;
  /**
   * Marks the phone as required — propagated to **both** the country
   * selector and the number field, since a phone with one half empty is
   * never a valid value (see `validateRequiredPassengerDetails`). Both
   * labels render the asterisk together.
   */
  required?: boolean;
  /**
   * Left-side icon. Pass `true` for the standard modern smartphone icon
   * (`PhoneAndroid`) — preferred so callers don't have to import an icon
   * just to get the canonical look. Pass any `ReactNode` to override.
   * Falsy means no icon. (Type is `ReactNode` because that already
   * includes `boolean`; the `=== true` check below is the explicit branch.)
   */
  leftIcon?: ReactNode;
  /**
   * Compact mode for genuinely narrow containers (e.g. the ~370px chat widget
   * panel). MUI's xs/md breakpoints key off the VIEWPORT, not this component's
   * container, so a narrow panel on a wide desktop viewport still gets the
   * cramped 3-column `md` layout. When `compact` is set, the field always uses the
   * STACKED layout (country selector on its own row, "+CC" + number on the next),
   * so the calling code STILL shows after a country is selected — just with room —
   * and it tells the country dropdown to drop the per-option calling code +
   * truncate long names (which otherwise overlap in the narrow popup). Off by
   * default — only the chat pre-chat form (the sole narrow usage) opts in; every
   * other usage keeps the standard viewport-responsive layout unchanged.
   */
  compact?: boolean;
  /**
   * Controls right of the number field, on its line in both layouts (the Business profile's Calls / WhatsApp
   * toggles, docs/website-builder/README.md O21).
   */
  numberEnd?: ReactNode;
};

export type PhoneInputProps<TFieldValues extends FieldValues> = Props<TFieldValues>;

/** FormTextField's own type, which `usePhoneInput` takes for the number (MUI's, or the chat widget's outlined one). */
export type FormTextFieldComponent = <TFieldValues extends FieldValues>(props: FormTextFieldProps<TFieldValues>) => ReactElement;

export function PhoneInput<TFieldValues extends FieldValues>(props: Props<TFieldValues>) {
  return usePhoneInput(FormTextField, CountrySelectorInput, props);
}

/**
 * PhoneInput, its number drawn with `NumberField` and its country with `CountryField` (see `useFormTextField`: the
 * chat widget's pre-chat form draws them outlined-only).
 */
export function usePhoneInput<TFieldValues extends FieldValues>(
  NumberField: FormTextFieldComponent,
  CountryField: CountrySelectorInputComponent,
  {
    countryCodeName,
    numberName,
    countriesWithNames,
    countryLabel,
    numberLabel,
    disabled,
    required,
    leftIcon,
    compact,
    numberEnd,
  }: Props<TFieldValues>,
): ReactElement {
  const { control, setValue } = useFormContext<TFieldValues>();
  const selectedCountryCode = String(useWatch({ control, name: countryCodeName }) ?? '').trim();
  const selectedCountry = countriesWithNames.find((country) => country.code === selectedCountryCode);
  const callingCode = selectedCountry ? `+${selectedCountry.callingCode}` : null;

  // Cache last non-null calling code so SmoothCollapse can keep rendering it
  // during the exit animation when the country deselects to ''.
  const lastCallingCodeRef = useRef<string>('');
  if (callingCode) lastCallingCodeRef.current = callingCode;
  const callingCodeForCollapse = callingCode ?? lastCallingCodeRef.current;

  const resolvedLeftIcon =
    leftIcon === true ? <PhoneAndroidIcon fontSize="small" /> : leftIcon;
  // One more column, after the number's, on the number's line.
  const hasNumberEnd = numberEnd !== undefined && numberEnd !== null && numberEnd !== false;

  return (
    <Box
      sx={{
        ...sharedFieldRowSx(),
        alignItems: 'flex-start',
      }}
    >
      {resolvedLeftIcon ? (
        <Box
          sx={[
            sharedLeftIconBoxSx(),
            {
              alignSelf: 'flex-start',
              // Override pt for PhoneInput's multi-row layout — vertical
              // alignment differs because the country selector + number
              // inputs span two rows on xs and one on md.
              pt: { xs: '54px', md: '16px' },
            },
          ]}
        >
          {resolvedLeftIcon}
        </Box>
      ) : null}

      <Box
        sx={{
          flex: 1,
          display: 'grid',
          // `compact` (narrow containers like the chat panel) forces the STACKED
          // layout regardless of the viewport breakpoint — MUI's `md` keys off the
          // viewport, not this container, so a ~370px panel on a desktop viewport
          // would otherwise get the cramped 3-column single row. Stacked = country
          // selector on its own full-width row 1, then "+CC" + number on row 2, so
          // the calling code STILL shows after a country is selected, just with room.
          gridTemplateColumns: compact
            ? `auto minmax(0, 1fr)${hasNumberEnd ? ' auto' : ''}`
            : {
                xs: `auto minmax(0, 1fr)${hasNumberEnd ? ' auto' : ''}`,
                md: `minmax(0, 1fr) auto minmax(0, 1fr)${hasNumberEnd ? ' auto' : ''}`,
              },
          // Small column gap (4px) flanks the calling-code track so the
          // country selector and number field still have visible breathing
          // room when no country is selected (auto track collapses to 0).
          // The SmoothCollapse content carries another 4px of padding on
          // each side, so the total around a visible "+CC" is the
          // standard 8px on each side.
          columnGap: 0.5,
          rowGap: 1,
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            // Stacked: the whole first line, over the calling code, the number and what follows it.
            gridColumn: compact ? '1 / -1' : { xs: '1 / -1', md: '1' },
            gridRow: '1',
            // Even with the clear (X) hidden, MUI Autocomplete reserves
            // ~39px on the input's right padding for the popup-arrow icon.
            // The arrow is absolutely positioned on top, and the input has
            // `overflow: hidden`, so dropping the padding to 0 reclaims the
            // space without text actually colliding with the arrow — a
            // ~28-character name like "Central African Republic" still
            // clips cleanly at the field edge. Worth ~30px of room for the
            // number field beside it.
            '& .MuiAutocomplete-inputRoot': { paddingRight: '6px !important' },
          }}
        >
          <CountryField<TFieldValues>
            name={countryCodeName}
            countriesWithNames={countriesWithNames}
            label={countryLabel}
            disabled={disabled}
            required={required}
            showCallingCode
            compact={compact}
          />
        </Box>

        {/* The in-field "+CC" track. Shown in every mode (including compact) once a
            country is selected — in compact it sits on row 2 beside the number. */}
        <Box
          sx={{
            gridColumn: compact ? '1' : { xs: '1', md: '2' },
            gridRow: compact ? '2' : { xs: '2', md: '1' },
            display: 'flex',
            alignItems: 'center',
            minHeight: 56,
          }}
        >
          <SmoothCollapse orientation="horizontal" in={!!callingCode}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 56, px: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ color: 'text.primary', whiteSpace: 'nowrap', fontWeight: 500 }}
              >
                {callingCodeForCollapse}
              </Typography>
            </Box>
          </SmoothCollapse>
        </Box>

        <Box
          sx={{
            gridColumn: compact ? '2' : { xs: '2', md: '3' },
            gridRow: compact ? '2' : { xs: '2', md: '1' },
            minWidth: 0,
          }}
        >
          <NumberField<TFieldValues>
            name={numberName}
            label={numberLabel}
            disabled={disabled}
            required={required}
            fullWidth
            slotProps={{ htmlInput: { maxLength: PHONE_NUMBER_MAX_LENGTH, inputMode: 'numeric' } }}
            onChange={(e) => {
              const sanitized = sanitizePhoneNumberInput(e.target.value);
              setValue(numberName, sanitized as PathValue<TFieldValues, Path<TFieldValues>>, { shouldDirty: true });
            }}
          />
        </Box>

        {hasNumberEnd ? (
          <Box
            sx={{
              gridColumn: compact ? '3' : { xs: '3', md: '4' },
              gridRow: compact ? '2' : { xs: '2', md: '1' },
              display: 'flex',
              alignItems: 'center',
              // Centred on the number field's 56 px, its helper text left below.
              minHeight: 56,
              pl: 1,
            }}
          >
            {numberEnd}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
